import React, { useState } from 'react';
import { FileClock, RotateCcw, Trash2, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

const fmt = (ts) => {
  if (!ts) return '-';
  try {
    return new Date(ts).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return ts;
  }
};

/**
 * Drafts recovery menu shown in a screen header.
 *
 * Props:
 * - drafts: [{ draft_id, label, bundle_count, updated_at }]
 * - onResume(draftId)
 * - onDelete(draftId)
 * - activeDraftId: the draft currently being edited (highlighted)
 * - saving: boolean (auto-save in flight)
 * - lastSavedAt: iso string of the last successful save
 * - variant: 'dark' (light text on dark header) | 'light'
 */
export const DraftsMenu = ({
  drafts = [],
  onResume,
  onDelete,
  activeDraftId = null,
  saving = false,
  lastSavedAt = null,
  variant = 'dark',
}) => {
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const count = drafts.length;

  const triggerClass =
    variant === 'dark'
      ? 'text-white hover:bg-white/10 border border-white/20'
      : 'text-[#2D5F4F] hover:bg-[#2D5F4F]/10 border border-[#2D5F4F]/30';

  const handleResume = async (id) => {
    setBusyId(id);
    try {
      await onResume?.(id);
      setOpen(false);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    setBusyId(id);
    try {
      await onDelete?.(id);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={triggerClass}
          data-testid="drafts-menu-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <FileClock className="w-4 h-4 mr-2" />
          )}
          Drafts
          {count > 0 && (
            <Badge
              className="ml-2 bg-[#F59E0B] text-white border-0"
              data-testid="drafts-count-badge"
            >
              {count}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="drafts-menu-panel">
        <div className="px-4 py-3 border-b border-[#E5DFD0] flex items-center justify-between">
          <span className="font-semibold text-[#1A1F2E] text-sm">Recover a Draft</span>
          {saving ? (
            <span className="text-xs text-[#8A8F9E] flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Saving…
            </span>
          ) : lastSavedAt ? (
            <span className="text-xs text-[#2D5F4F] flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Saved {fmt(lastSavedAt)}
            </span>
          ) : null}
        </div>

        {count === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-[#8A8F9E]" data-testid="drafts-empty">
            No saved drafts. Your progress is auto-saved as you scan.
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto divide-y divide-[#F0EBDD]">
            {drafts.map((d) => (
              <div
                key={d.draft_id}
                className={`px-4 py-3 ${
                  d.draft_id === activeDraftId ? 'bg-[#F5F1E8]' : 'bg-white'
                }`}
                data-testid={`draft-row-${d.draft_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1A1F2E] truncate">
                      {d.label || 'Untitled draft'}
                      {d.draft_id === activeDraftId && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-[#2D5F4F]">
                          current
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-[#8A8F9E] mt-0.5">
                      {d.bundle_count} bundle{d.bundle_count === 1 ? '' : 's'} · {fmt(d.updated_at)}
                    </p>
                    {d.last_updated_by_name && (
                      <p className="text-[11px] text-[#B08A3E] mt-0.5 truncate" data-testid={`draft-editor-${d.draft_id}`}>
                        Last worked on by {d.last_updated_by_name}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 px-2 border-[#2D5F4F] text-[#2D5F4F] hover:bg-[#2D5F4F] hover:text-white"
                      disabled={busyId === d.draft_id}
                      onClick={() => handleResume(d.draft_id)}
                      data-testid={`draft-resume-${d.draft_id}`}
                    >
                      {busyId === d.draft_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3.5 h-3.5" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 px-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                      disabled={busyId === d.draft_id}
                      onClick={() => handleDelete(d.draft_id)}
                      data-testid={`draft-delete-${d.draft_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default DraftsMenu;
